import { useId, type ReactNode, type Ref } from 'react';
import { chapiPalette, chapiPivots, chapiRig, chapiViewBox, markArt, LEAF_PATH, MOUTH_PATH, viewBoxAttr, type ArtShape } from '@chapsi/ui-tokens/motion';

/** Draws the shared vector art (packages/ui-tokens/src/chapi-art.ts) as SVG elements. */
function Shapes({ shapes, ids }: { shapes: readonly ArtShape[]; ids: Ids }) {
  return <>{shapes.map((s, i) => <Shape key={i} s={s} ids={ids} />)}</>;
}

export interface Ids { or: string; uv: string; mouth: string }

const paint = (fill: string | undefined, ids: Ids) => (fill === 'url:or' ? `url(#${ids.or})` : fill === 'url:uv' ? `url(#${ids.uv})` : (fill ?? 'none'));

function Shape({ s, ids }: { s: ArtShape; ids: Ids }) {
  switch (s.kind) {
    case 'path':
      return (
        <path
          d={s.d}
          fill={paint(s.fill, ids)}
          stroke={s.stroke}
          strokeWidth={s.width}
          strokeLinecap={s.round ? 'round' : undefined}
          strokeLinejoin={s.round || s.joinRound ? 'round' : undefined}
          opacity={s.opacity}
          transform={s.transform}
        />
      );
    case 'ellipse':
      return <ellipse cx={s.cx} cy={s.cy} rx={s.rx} ry={s.ry} fill={paint(s.fill, ids)} opacity={s.opacity} transform={s.transform} clipPath={s.clipMouth ? `url(#${ids.mouth})` : undefined} />;
    case 'circle':
      return <circle cx={s.cx} cy={s.cy} r={s.r} fill={paint(s.fill, ids)} />;
    case 'rect':
      return <rect x={s.x} y={s.y} width={s.width} height={s.height} rx={s.rx} fill={paint(s.fill, ids)} />;
    case 'text':
      return <text x={s.x} y={s.y} fontSize={s.size} fill={s.fill} fontFamily="var(--font-display)" fontWeight={700}>{s.text}</text>;
    case 'group':
      return <g transform={s.transform}><Shapes shapes={s.children} ids={ids} /></g>;
  }
}

export function useIds(): Ids {
  const base = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  return { or: `${base}or`, uv: `${base}uv`, mouth: `${base}m` };
}

export function Gradients({ ids }: { ids: Ids }) {
  const [o1, o2] = chapiPalette.orange;
  const [u1, u2, u3] = chapiPalette.ultraviolet;
  return (
    <>
      <linearGradient id={ids.or} x1="0" y1="1" x2="1" y2="0"><stop offset="0" stopColor={o1} /><stop offset="1" stopColor={o2} /></linearGradient>
      <linearGradient id={ids.uv} x1="0.15" y1="0" x2="0.85" y2="1"><stop offset="0" stopColor={u1} /><stop offset="0.45" stopColor={u2} /><stop offset="1" stopColor={u3} /></linearGradient>
    </>
  );
}

export interface ChapiLayers {
  root?: Ref<SVGGElement>;
  whole?: Ref<SVGGElement>;
  armL?: Ref<SVGGElement>;
  armR?: Ref<SVGGElement>;
  head?: Ref<SVGGElement>;
  eyes?: Ref<SVGGElement>;
  cheeks?: Ref<SVGGElement>;
}

export type ChapiPose = 'awake' | 'asleep';
export type ChapiFace = 'smile' | 'worried';

/**
 * Chapi in layers. `awake` waves with open eyes (they blink); `asleep` rests with closed eyes, the
 * headset and the drawn z's. `face="worried"` frowns (task delayed, off sick); `flushed` reddens the
 * cheeks; `armRTurn` holds the raised arm at an angle; `front` draws props over Chapi. Decorative:
 * hidden from assistive technology.
 */
export function ChapiArt({ size, pose = 'awake', face = 'smile', flushed = false, armRTurn, eyesRest, headset = pose === 'asleep', layers = {}, front, className }: {
  size: number;
  pose?: ChapiPose;
  face?: ChapiFace;
  flushed?: boolean;
  armRTurn?: number;
  /** Eyes held part shut (off sick); a blink animates them from there. */
  eyesRest?: number;
  headset?: boolean;
  layers?: ChapiLayers;
  front?: ReactNode;
  className?: string;
}) {
  const ids = useIds();
  const vb = pose === 'asleep' ? chapiViewBox.asleep : headset ? chapiViewBox.headset : chapiViewBox.plain;
  const eyes = pose === 'asleep' ? chapiRig.face.asleep.eyes : chapiRig.face.awake.eyes;
  const rest = pose === 'asleep' ? chapiRig.face.asleep.rest : face === 'worried' ? chapiRig.face.worried.rest : chapiRig.face.awake.rest;
  const arms = pose === 'asleep' ? chapiRig.arms.rest : chapiRig.arms.wave;
  const sh = chapiPivots.armR;
  return (
    <svg className={className} viewBox={viewBoxAttr(vb)} width={size} height={Math.round((size * vb.height) / vb.width)} overflow="visible" aria-hidden="true" focusable="false">
      <defs>
        <Gradients ids={ids} />
        <clipPath id={ids.mouth}><path d={MOUTH_PATH} /></clipPath>
      </defs>
      <g ref={layers.root}>
        <g ref={layers.whole}>
          <Shapes shapes={chapiRig.legs} ids={ids} />
          <g ref={layers.armL}><Shapes shapes={arms.left} ids={ids} /></g>
          <g transform={armRTurn ? `rotate(${armRTurn} ${sh.x} ${sh.y})` : undefined}>
            <g ref={layers.armR}><Shapes shapes={arms.right} ids={ids} /></g>
          </g>
          <g ref={layers.head}>
            <Shapes shapes={chapiRig.head} ids={ids} />
            <g transform={eyesRest ? `translate(0 ${chapiPivots.eyes.y}) scale(1 ${eyesRest}) translate(0 ${-chapiPivots.eyes.y})` : undefined}>
              <g ref={layers.eyes}><Shapes shapes={eyes} ids={ids} /></g>
            </g>
            <Shapes shapes={rest} ids={ids} />
            <g ref={layers.cheeks}><Shapes shapes={flushed ? chapiRig.face.flushed : chapiRig.face.cheeks} ids={ids} /></g>
            {headset && <Shapes shapes={chapiRig.headset} ids={ids} />}
          </g>
          <Shapes shapes={chapiRig.body} ids={ids} />
          {pose === 'asleep' && <Shapes shapes={chapiRig.sleepZs} ids={ids} />}
          {front}
        </g>
      </g>
    </svg>
  );
}

/** A whole drawing from a shape list (the holiday and birthday poses), moved as one. */
export function FigureArt({ size, art, root, children }: { size: number; art: { viewBox: { x: number; y: number; width: number; height: number }; shapes: readonly ArtShape[]; mouth?: string }; root?: Ref<SVGGElement>; children?: ReactNode }) {
  const ids = useIds();
  const vb = art.viewBox;
  return (
    <svg viewBox={viewBoxAttr(vb)} width={size} height={Math.round((size * vb.height) / vb.width)} overflow="visible" aria-hidden="true" focusable="false">
      <defs>
        <Gradients ids={ids} />
        <clipPath id={ids.mouth}><path d={art.mouth ?? MOUTH_PATH} /></clipPath>
      </defs>
      <g ref={root}>
        <Shapes shapes={art.shapes} ids={ids} />
        {children}
      </g>
    </svg>
  );
}

/** The two-leaf mark (the loading spinner). */
export function MarkArt({ size, leafA, leafB }: { size: number; leafA?: Ref<SVGPathElement>; leafB?: Ref<SVGPathElement> }) {
  const ids = useIds();
  const vb = markArt.viewBox;
  return (
    <svg viewBox={viewBoxAttr(vb)} width={size} height={Math.round((size * vb.height) / vb.width)} overflow="visible" aria-hidden="true" focusable="false">
      <defs><Gradients ids={ids} /></defs>
      <g transform={`translate(${markArt.offset.x} ${markArt.offset.y})`}>
        <path ref={leafA} d={LEAF_PATH} fill={`url(#${ids.or})`} />
        <g transform={`translate(${markArt.offsetB.x} ${markArt.offsetB.y})`}><path ref={leafB} d={LEAF_PATH} fill={`url(#${ids.uv})`} /></g>
      </g>
    </svg>
  );
}
